import { useMutation } from '@apollo/client';

import type { CreateSearchAlertMutation } from '@xing-com/crate-jobs-graphql';
import { CreateSearchAlertDocument } from '@xing-com/crate-jobs-graphql';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { useViewerData } from '@xing-com/hub';

import { addAlertToCachedList } from './apollo-helpers';
import type { SearchAlertHookParams, UseCreateSearchAlert } from './types';

const createOptimisticUpdate = (
  queryId: string
): CreateSearchAlertMutation => ({
  __typename: 'Mutation',
  createSearchAlert: {
    __typename: 'SearchAlertCreationSuccess',
    alert: {
      __typename: 'SearchAlert',
      id: 'optimistic-response-id',
      globalId: 'optimistic-response-global-id',
      queryId: queryId,
      query: null,
      name: 'optimistic-response-name',
      newJobCount: null,
      createdAt: new Date().toString(),
      visitedAt: new Date().toString(),
    },
  },
});

const DEFAULT_PARAMS = {
  disableOptimisticUpdate: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onError: (): void => {},
};

export const useCreateSearchAlert = (
  params?: SearchAlertHookParams
): UseCreateSearchAlert => {
  const { disableOptimisticUpdate, onError } = {
    ...DEFAULT_PARAMS,
    ...(params ?? {}),
  };

  const [createSearchAlertX1] = useMutation(CreateSearchAlertDocument);
  const userId = useViewerData().data?.user?.id;
  const trackNwt = useJobsNwtTracking();

  return ({ queryId, nwtTrackingOptions, onCompleted, origin }) => {
    if (queryId) {
      createSearchAlertX1({
        variables: { queryId, origin },
        update: addAlertToCachedList(userId),
        optimisticResponse: disableOptimisticUpdate
          ? undefined
          : createOptimisticUpdate(queryId),
        onCompleted: (response) => {
          if (
            response.createSearchAlert?.__typename ===
            'SearchAlertErrorResponse'
          ) {
            onError();
          } else {
            if (nwtTrackingOptions) {
              trackNwt({
                event: 'activated',
                eventSchema: 'basic',
                page: 'jobs_search/serp',
                element: 'search_alert',
                itemUrn: response.createSearchAlert?.alert.globalId,
                ...nwtTrackingOptions,
              });
            }
          }

          onCompleted?.(response);
        },
        onError,
      });
    }
  };
};
